/**
 * 当前登录身份的**唯一缓存**（v0.47）。
 *
 * ★ 为什么用 zustand store 而不是 React Context / 一堆 props：
 *    需要在 FeatureTable / DrawingTree / PartMasterPage / Canvas 这些**深处**的
 *    组件里问"我能不能编辑"。用 Context 要改每个组件的签名；用 props 要穿透
 *    五六层。store 是**一份**状态，各处 `useCan('engineer')` 各自订阅 ——
 *    唯一状态 + 单向读取，不会出现"两处对同一个人的权限判断不一样"。
 *
 * ⚠️ 绝对不要在这里再存一份"能不能编辑"的推导结果（例如 `isAdmin` 字段）：
 *    那就是同一个语义的第二份实现 —— 改了角色忘了同步它，界面就会出现
 *    "有用户管理入口但进不去"这类灵异现象。**只有 `user` 一个真相**，
 *    所有判断都从它现算（见 `useCan`）。
 */
import { create } from 'zustand';
import { roleAtLeast, type Role } from '../lib/auth';

export interface AuthUser {
  id: number;
  username: string;
  display_name: string | null;
  role: Role | string;
  role_cn: string;
  is_active: boolean;
  must_change_password: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  last_login_at?: string | null;
  created_by?: string | null;
}

interface AuthState {
  /** `GET /api/auth/me` 是否已经回来过 —— 没回来之前不要渲染"未登录"，否则会闪一下登录页 */
  ready: boolean;
  /** 后端是否启用了鉴权。false = 本地/测试环境，直接放行 */
  authEnabled: boolean;
  user: AuthUser | null;
  /** 角色中文名表（后端下发，前端不再硬编码第二份） */
  roles: Record<string, string>;

  setMe: (p: { authEnabled: boolean; user: AuthUser | null; roles?: Record<string, string> }) => void;
  setUser: (u: AuthUser | null) => void;
  /** 会话失效（401）时调用：清身份但**不清 ready/authEnabled**，界面会切到登录页 */
  markLoggedOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  ready: false,
  authEnabled: true,
  user: null,
  roles: {},
  setMe: ({ authEnabled, user, roles }) =>
    set((s) => ({ ready: true, authEnabled, user, roles: roles ?? s.roles })),
  setUser: (u) => set({ user: u, ready: true }),
  markLoggedOut: () => set({ user: null, ready: true }),
}));

/**
 * 我是否有 `min` 级权限。
 *
 * ★ 未启用鉴权时**恒为 true**：那是本机/测试环境，后端中间件整个关闭了，
 *   界面若还按 viewer 渲染，本地开发就看不到编辑功能 —— 会逼着人去把权限
 *   判断写得"本地一套、线上一套"，那正是两份实现。
 */
export function useCan(min: Role): boolean {
  const authEnabled = useAuthStore((s) => s.authEnabled);
  const user = useAuthStore((s) => s.user);
  if (!authEnabled) return true;
  if (!user) return false;
  return roleAtLeast(user.role, min);
}

/** 常用角色的快捷判断（都从同一个 `user` 现算）。 */
export const useIsAdmin = () => useCan('admin');
export const useCanEdit = () => useCan('engineer');
export const useCanMeasure = () => useCan('inspector');
